import { useEffect } from "react";
import RussellvilleHeroSection from "./components/HeroSection";
import RussellvilleStickyNav from "./components/StickyNav";
import RussellvilleProcessingTimeSection from "./components/ProcessingTimeSection";
import RussellvilleServicesSection from "./components/ServicesSection";
import RussellvilleLocalAreaSection from "./components/LocalAreaSection";
import RussellvilleTravelTimingSection from "./components/TravelTimingSection";
import RussellvilleDestinationsSection from "./components/DestinationsSection";
import RussellvilleTestimonialsSection from "./components/TestimonialsSection";
import RussellvilleAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import RussellvilleComparisonSection from "./components/ComparisonSection";
import RussellvilleMistakesSection from "./components/MistakesSection";
import RussellvilleHowItWorksSection from "./components/HowItWorksSection";
import RussellvilleFAQSection, { faqs } from "./components/FAQSection";
import RussellvilleHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import RussellvilleFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function RussellvillePage() {
  useEffect(() => {
    document.title = "Emergency Passport Russellville, AR — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/russellville/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Russellville, AR — as fast as 24 hours. Serving ATU students, Arkansas Nuclear One professionals, and River Valley mission teams. A+ BBB rated, registered State Dept. courier.");
    setMeta("geo.placename", "Russellville, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "35.2784;-93.1338");
    setMeta("ICBM", "35.2784, -93.1338");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
          { "@type": "ListItem", position: 3, name: "Russellville Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Russellville, AR and the Arkansas River Valley. Serving ATU students, Arkansas Nuclear One professionals, and mission teams.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Russellville", containedInPlace: { "@type": "State", name: "Arkansas" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Russellville" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <RussellvilleHeroSection />
      <RussellvilleStickyNav />
      <RussellvilleProcessingTimeSection />
      <RussellvilleServicesSection />
      <RussellvilleLocalAreaSection />
      <RussellvilleTravelTimingSection />
      <RussellvilleDestinationsSection />
      <RussellvilleTestimonialsSection />
      <RussellvilleAcceptanceFacilitiesSection />
      <RussellvilleComparisonSection />
      <RussellvilleMistakesSection />
      <RussellvilleHowItWorksSection />
      <RussellvilleFAQSection />
      <RussellvilleHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/russellville" state="AR" />
      <RussellvilleFinalCTA />
    </div>
  );
}